import type { OperatorSlug } from '@ev/shared'
import { create } from 'zustand'
import {
  EMPTY_FILTERS,
  type FilterFacet,
  type FilterFacetValue,
  type Filters,
  toggleFacetValue,
} from './filters'
import { EMPTY_RESULTS, type LoadPhase, type MapResults, sameResults } from './results'
import { decodeUrlState, EMPTY_URL_STATE, encodeUrlState, type UrlState } from './urlState'

/**
 * The single client-side store.
 *
 * It holds what the URL holds - filters, the text query, the selected site - so
 * the whole view is shareable, plus one derived slice: the results the map
 * publishes after filtering, which the panel and the search box read. Fetched
 * artifacts are *not* stored here; they belong to the data layer in `../data`.
 *
 * Selection is one field. A marker click, a search result, a keyboard-list
 * choice and a pasted link all write `selectedSiteId` and nothing else, and
 * everything that reacts to a selection - the detail panel opening, the camera
 * flying, the highlight ring, the URL - reacts to that one field changing.
 * There is no second notion of "what is selected" anywhere in the app.
 */

export interface AppState extends UrlState {
  /** Published by the map; read by the filter panel and the search box. */
  readonly results: MapResults
  publishResults(results: MapResults): void

  setQuery(query: string): void
  setSelectedSiteId(siteId: string | null): void

  setFilters(filters: Filters): void
  toggleFilter<F extends FilterFacet>(facet: F, value: FilterFacetValue<F>): void
  setMinPowerKw(minPowerKw: number | null): void
  resetFilters(): void

  /** Replace the whole state from a query string, e.g. on first paint or popstate. */
  hydrateFromSearch(search: string): void
  /** The query string that reproduces the current view, without the leading `?`. */
  toSearch(): string
}

export const useAppStore = create<AppState>((set, get) => ({
  ...EMPTY_URL_STATE,
  results: EMPTY_RESULTS,

  publishResults: (results) => {
    if (!sameResults(get().results, results)) set({ results })
  },

  setQuery: (query) => set({ query }),

  setSelectedSiteId: (selectedSiteId) => set({ selectedSiteId }),

  setFilters: (filters) => set({ filters }),

  toggleFilter: (facet, value) => set({ filters: toggleFacetValue(get().filters, facet, value) }),

  setMinPowerKw: (minPowerKw) => set({ filters: { ...get().filters, minPowerKw } }),

  resetFilters: () => set({ filters: EMPTY_FILTERS }),

  hydrateFromSearch: (search) => set(decodeUrlState(search)),

  toSearch: () => {
    const { filters, query, selectedSiteId } = get()
    return encodeUrlState({ filters, query, selectedSiteId })
  },
}))

/**
 * Selector helpers.
 *
 * Components subscribe to one field rather than to `results` as a whole,
 * because the map republishes the viewport count on every camera move and a
 * panel that only cares about per-operator counts must not re-render while
 * somebody drags the map.
 */
export const selectFilters = (state: AppState): Filters => state.filters
export const selectQuery = (state: AppState): string => state.query
export const selectSelectedSiteId = (state: AppState): string | null => state.selectedSiteId
export const selectResults = (state: AppState): MapResults => state.results
export const selectResultStatus = (state: AppState): LoadPhase => state.results.status
export const selectMatchingCount = (state: AppState): number => state.results.matching
export const selectVisibleCount = (state: AppState): number => state.results.visible
export const selectTotalCount = (state: AppState): number => state.results.total
export const selectMatchingIds = (state: AppState): readonly string[] | null =>
  state.results.matchingIds
export const selectOperatorCounts = (
  state: AppState,
): Readonly<Partial<Record<OperatorSlug, number>>> => state.results.byOperator
